import { useCallback, useEffect, useRef, useState } from 'react'

import { getGame } from 'services/games.service'

import { GameSession } from '../types'

// ---------------------------------------------------------------------------
// useGameSession
// Owns the shared fields: company, topic, gameId, and initial load.
// When the API starts returning persisted scenario data, add it to the
// returned object here and consume it in useScenario's useState initializer.
// ---------------------------------------------------------------------------
export function useGameSession(
	gameId?: string,
	onError?: (message: string) => void
): GameSession {
	const [companyName, setCompanyName] = useState('')
	const [topic, setTopic] = useState('')
	const [companyProblem, setCompanyProblem] = useState('')
	const [sessionGameId, setSessionGameId] = useState<string | null>(null)
	const [gameTitle, setGameTitle] = useState('')
	const [endTime, setEndTime] = useState<string | null>(null)
	const [isReady, setIsReady] = useState(false)

	const onErrorRef = useRef(onError)
	onErrorRef.current = onError

	useEffect(() => {
		const controller = new AbortController()
		setIsReady(false)

		const loadGame = async () => {
			try {
				if (!gameId) {
					setCompanyName(
						localStorage.getItem('innovationCompany') ?? ''
					)
					setCompanyProblem(
						localStorage.getItem('companyProblem') ?? ''
					)
					setTopic(localStorage.getItem('innovationTopic') ?? '')
					setSessionGameId(null)
				} else {
					const response = await getGame(gameId, controller.signal)
					const game = response.data
					// TODO: When the API returns persisted scenario data,
					// store it here and expose it from this hook so useScenario
					// can seed its initial state from it.
					setCompanyName(game.innovationCompany)
					setTopic(game.innovationTopic)
					setCompanyProblem(game.companyProblem)
					setSessionGameId(game.gameId)
					setGameTitle(game.gameTitle)
					setEndTime(game.endTime)
				}
			} catch (error) {
				if ((error as Error).name !== 'CanceledError') {
					onErrorRef.current?.(
						`Failed to load game: ${(error as Error).message}`
					)
				}
			} finally {
				if (!controller.signal.aborted) {
					setIsReady(true)
				}
			}
		}

		loadGame()
		return () => controller.abort()
	}, [gameId])

	// Persist free-session fields to localStorage so they survive a page reload.
	// Only when there is no gameId — game-linked sessions get their data from the API.
	useEffect(() => {
		if (gameId || !isReady) return
		const debounce = setTimeout(() => {
			localStorage.setItem('innovationCompany', companyName)
			localStorage.setItem('companyProblem', companyProblem)
			localStorage.setItem('innovationTopic', topic)
		}, 200)
		return () => clearTimeout(debounce)
	}, [gameId, isReady, companyName, companyProblem, topic])

	const resetAll = useCallback(() => {
		setCompanyName('')
		setTopic('')
		setCompanyProblem('')
	}, [])

	return {
		companyName,
		companyProblem,
		endTime,
		gameId: sessionGameId,
		gameTitle,
		isReady,
		resetAll,
		setCompanyName,
		setCompanyProblem,
		setTopic,
		topic,
	}
}
